import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import SaveIcon from "@material-ui/icons/Save";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  paper: {
    "& .MuiDialog-paper": {
      width: 460,
      maxWidth: "95vw",
    },
  },
  header: {
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  section: {
    padding: "16px",
  },
  sectionTitle: {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
    marginBottom: 12,
  },
  saveBtn: {
    minWidth: 80,
  },
  leaveBtn: {
    color: "#dc2626",
    borderColor: "#dc2626",
    "&:hover": { backgroundColor: "#fee2e2", borderColor: "#dc2626" },
  },
  content: {
    padding: 0,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
}));

const EPHEMERAL_OPTIONS = [
  { value: 0, label: "Desativado" },
  { value: 86400, label: "24 horas" },
  { value: 604800, label: "7 dias" },
  { value: 7776000, label: "90 dias" },
];

const GroupSettingsModal = ({ open, onClose, groupJid, whatsappId, groupName: initialName }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});

  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [announcement, setAnnouncement] = useState(false);
  const [locked, setLocked] = useState(false);
  const [ephemeral, setEphemeral] = useState(0);
  const [leavingGroup, setLeavingGroup] = useState(false);

  useEffect(() => {
    if (open && groupJid) {
      setSubject(initialName || "");
      loadMetadata();
    }
    // eslint-disable-next-line
  }, [open, groupJid]);

  const loadMetadata = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/groups/${encodeURIComponent(groupJid)}/metadata`, {
        params: { whatsappId },
        timeout: 20000,
      });
      setSubject(data.subject || "");
      setDescription(data.desc || data.description || "");
      setAnnouncement(data.announce === true || data.announce === "true");
      setLocked(data.restrict === true || data.restrict === "true");
      setEphemeral(data.ephemeralDuration || 0);
    } catch {
      toast.error("Erro ao carregar configurações do grupo");
    } finally {
      setLoading(false);
    }
  };

  const save = async (action, payload) => {
    setSaving((prev) => ({ ...prev, [action]: true }));
    try {
      const encodedJid = encodeURIComponent(groupJid);
      if (action === "subject") {
        await api.put(`/groups/${encodedJid}/subject`, { whatsappId, subject });
        toast.success("Nome do grupo atualizado!");
      } else if (action === "description") {
        await api.put(`/groups/${encodedJid}/description`, { whatsappId, description });
        toast.success("Descrição atualizada!");
      } else if (action === "announcement") {
        await api.put(`/groups/${encodedJid}/settings`, {
          whatsappId,
          setting: payload ? "announcement" : "not_announcement",
        });
        setAnnouncement(payload);
        toast.success(payload ? "Apenas admins podem enviar mensagens" : "Todos podem enviar mensagens");
      } else if (action === "locked") {
        await api.put(`/groups/${encodedJid}/settings`, {
          whatsappId,
          setting: payload ? "locked" : "unlocked",
        });
        setLocked(payload);
        toast.success(payload ? "Apenas admins podem editar info do grupo" : "Todos podem editar info do grupo");
      } else if (action === "ephemeral") {
        await api.put(`/groups/${encodedJid}/ephemeral`, { whatsappId, expiration: payload });
        setEphemeral(payload);
        const label = EPHEMERAL_OPTIONS.find((o) => o.value === payload)?.label;
        toast.success(`Mensagens temporárias: ${label}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao salvar");
    } finally {
      setSaving((prev) => ({ ...prev, [action]: false }));
    }
  };

  const handleLeave = async () => {
    if (!window.confirm(`Sair do grupo "${subject}"?`)) return;
    setLeavingGroup(true);
    try {
      await api.delete(`/groups/${encodeURIComponent(groupJid)}/leave`, {
        data: { whatsappId },
      });
      toast.success("Você saiu do grupo");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao sair do grupo");
    } finally {
      setLeavingGroup(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className={classes.paper} maxWidth={false}>
      <Box className={classes.header}>
        <Box>
          <Typography style={{ fontWeight: 600, fontSize: "0.95rem" }}>Configurações do Grupo</Typography>
          <Typography variant="caption" color="textSecondary">{initialName}</Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          <Tooltip title="Recarregar">
            <span>
              <IconButton size="small" onClick={loadMetadata} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </Box>

      <DialogContent className={classes.content}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Nome */}
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>Nome do grupo</Typography>
              <Box display="flex" style={{ gap: 8 }}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  value={subject}
                  onChange={(e) => { const v = e.target.value; setSubject(v); }}
                  inputProps={{ maxLength: 100 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  className={classes.saveBtn}
                  onClick={() => save("subject")}
                  disabled={!!saving.subject || !subject.trim()}
                  startIcon={saving.subject ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                >
                  Salvar
                </Button>
              </Box>
            </Box>

            <Divider />

            {/* Descrição */}
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>Descrição</Typography>
              <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => { const v = e.target.value; setDescription(v); }}
                  inputProps={{ maxLength: 512 }}
                  placeholder="Descrição do grupo..."
                />
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => save("description")}
                    disabled={!!saving.description}
                    startIcon={saving.description ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                  >
                    Salvar descrição
                  </Button>
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Permissões */}
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>Permissões</Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={announcement}
                    onChange={(e) => save("announcement", e.target.checked)}
                    color="primary"
                    size="small"
                    disabled={!!saving.announcement}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      Apenas admins enviam mensagens
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Modo anúncio — participantes não podem enviar
                    </Typography>
                  </Box>
                }
                style={{ display: "flex", margin: "0 0 12px 0", alignItems: "flex-start" }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={locked}
                    onChange={(e) => save("locked", e.target.checked)}
                    color="primary"
                    size="small"
                    disabled={!!saving.locked}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      Apenas admins editam informações
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Impede participantes de alterar nome, descrição e foto
                    </Typography>
                  </Box>
                }
                style={{ display: "flex", margin: 0, alignItems: "flex-start" }}
              />
            </Box>

            <Divider />

            {/* Mensagens temporárias */}
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>Mensagens temporárias</Typography>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Duração</InputLabel>
                <Select
                  value={ephemeral}
                  onChange={(e) => { const v = e.target.value; save("ephemeral", v); }}
                  label="Duração"
                  disabled={!!saving.ephemeral}
                >
                  {EPHEMERAL_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions style={{ padding: "12px 16px", justifyContent: "flex-start" }}>
        <Button
          variant="outlined"
          size="small"
          className={classes.leaveBtn}
          onClick={handleLeave}
          disabled={leavingGroup}
          startIcon={leavingGroup ? <CircularProgress size={14} color="inherit" /> : <ExitToAppIcon />}
        >
          Sair do grupo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupSettingsModal;
